import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/cn'
import styles from '../styles/inspector.module.scss'

export interface InspectorPanelProps {
  icon: React.ReactNode
  title: React.ReactNode
  /** Short summary kept visible when the panel is collapsed — the span of a
   * caption, the length of a clip, the shape of the crop. It is the whole
   * reason collapsing is useful: the header still answers "what is this". */
  meta?: React.ReactNode
  /** Controls that belong to the header rather than to the body (delete,
   * visibility). Rendered *outside* the collapse button — nesting them
   * inside it would be invalid markup and would collapse the panel on every
   * click. */
  actions?: React.ReactNode
  children: React.ReactNode
}

/**
 * The frame every side-panel inspector sits in.
 *
 * Clicking the header collapses the panel. With four of these able to stack
 * — framing, clip, caption, colour — plus the clip list underneath, an open
 * panel you are done with is just something between you and the list.
 */
export function InspectorPanel({ icon, title, meta, actions, children }: InspectorPanelProps) {
  const [collapsed, setCollapsed] = useState(false)

  return (
    <div className={styles.inspector}>
      <div className={styles.inspectorHead}>
        <button
          type="button"
          className={styles.inspectorToggle}
          onClick={() => {
            setCollapsed((prev) => !prev)
          }}
          aria-expanded={!collapsed}
          title={collapsed ? 'Expandir' : 'Minimizar'}
        >
          {icon}
          <span className={styles.inspectorTitle}>{title}</span>
          {meta ? <span className={styles.inspectorTime}>{meta}</span> : null}
          <ChevronDown
            className={cn(styles.chevron, collapsed && styles.chevronCollapsed)}
            aria-hidden
          />
        </button>
        {actions}
      </div>
      {collapsed ? null : <div className={styles.inspectorBody}>{children}</div>}
    </div>
  )
}
